import { isEqual } from 'lodash';
import { useCallback, useMemo } from 'react';
import { get, UseFormProps } from 'react-hook-form';

import {
  DEFAULT_FIELD_SCHEMA,
  FieldSchemaEditorSupportedTypes,
  fieldSchemasToFormValues,
  formValuesToFieldSchemas,
  getAllFieldKeys,
  getAllFields,
  replaceNestedField,
  validateAllSchemas,
  validateSingleFieldSchema,
} from 'dashboard/components-lib/FieldSchemaEditor';
import { Identifier } from 'dashboard/models/identifier';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface FieldSchemaEditorFormValues {
  fieldSchemaMap: Record<
    string,
    { key: string; path: string; fieldSchema: FieldSchemaEditorSupportedTypes }
  >;
  identifiers?: Identifier[];
}

// A wrapper around use form securely hook to handle the logic and complexities for field schema editor
function useFieldSchemaEditor(
  props: UseFormProps<FieldSchemaEditorFormValues> & {
    // Override type default values to require passing in default values
    defaultValues: FieldSchemaEditorFormValues;
  },
) {
  // Current form values are treated as cached values that can be reset to last saved form values
  // if user cancels editing current field schema or reset to new values when user saves currently editing field schema
  const { formState, watch, reset, setError, clearErrors, setValue, ...formProps } =
    useFormSecurely<FieldSchemaEditorFormValues>(props);

  const activeFieldSchemaMap = watch('fieldSchemaMap');
  const activeFieldSchemas = formValuesToFieldSchemas(activeFieldSchemaMap);
  const activeIdentifiers = watch('identifiers');

  const preexistingFieldKeys = useMemo(
    () => getAllFieldKeys(formValuesToFieldSchemas(props.defaultValues.fieldSchemaMap)),
    [props.defaultValues],
  );

  const formFieldKeys = useMemo(() => getAllFieldKeys(activeFieldSchemas), [activeFieldSchemas]);

  const deleteDraftField = (key: string) => {
    if (preexistingFieldKeys.includes(key)) {
      return;
    }

    const { newSchemas: nextSchemas } = replaceNestedField(activeFieldSchemas, key, null);
    const nextIdentifiers = (activeIdentifiers || []).filter(
      (identifier) => identifier.key !== key,
    );

    reset(
      {
        fieldSchemaMap: fieldSchemasToFormValues(nextSchemas),
        identifiers: nextIdentifiers,
      },
      { keepIsValid: true },
    );
  };

  const selectedFieldSchema = (selectedFieldSchemaPath: string) => {
    if (!selectedFieldSchemaPath) {
      return null;
    }
    return getAllFields(activeFieldSchemas).find(({ key }) => key === selectedFieldSchemaPath)
      ?.fieldSchema;
  };

  const validateAllFieldSchemas = () => {
    const errKeys = validateAllSchemas(activeFieldSchemas);
    errKeys.forEach((errKey) => {
      setError(`fieldSchemaMap.${errKey}`, {
        type: 'custom',
      });
    });
  };

  const isFieldSchemasDirty = useMemo(() => {
    return !isEqual(props.defaultValues.fieldSchemaMap, activeFieldSchemaMap);
  }, [activeFieldSchemaMap, props.defaultValues.fieldSchemaMap]);

  // We get errored keys from activeFieldSchemaMap because it better preserves the shape of the key
  // For example
  // activeFieldSchemaMap =
  // {
  //     "hash": {
  //         "key": "hash",
  //         "path": "0",
  //         "fieldSchema": {
  //             "type": "hash",
  //             "label": "hash",
  //             "key": "hash",
  //             "default-value": null,
  //             "config": {
  //                 "item-schemas": [
  //                     {}
  //                 ]
  //             }
  //         }
  //     },
  //     "hash.0": {
  //         "key": "hash.0",
  //         "path": "0.config.item-schemas.0",
  //         "fieldSchema": {}
  //     }
  // }
  // corresponds to the following formState.errors
  // {
  //     "fieldSchemaMap": {
  //         "hash": [
  //             {
  //                 "type": "custom"
  //             }
  //         ]
  //     }
  // }
  const erroredKeys = Object.keys(activeFieldSchemaMap).reduce((errKeys, fieldSchemaKey) => {
    // Only get nested keys that have an error on it by required attribute type
    if (get(formState.errors, `fieldSchemaMap.${fieldSchemaKey}`)?.type) {
      return [...errKeys, fieldSchemaKey];
    } else {
      return errKeys;
    }
  }, [] as string[]);

  const addNewFieldSchema = () => {
    setValue(
      'fieldSchemaMap',
      fieldSchemasToFormValues(activeFieldSchemas.concat(DEFAULT_FIELD_SCHEMA)),
      { shouldDirty: true, shouldValidate: true },
    );
    return { fieldSchemaPath: `${activeFieldSchemas.length}` };
  };

  const updateFieldSchemas = (nextFieldSchema: FieldSchemaEditorSupportedTypes[]) => {
    setValue('fieldSchemaMap', fieldSchemasToFormValues(nextFieldSchema), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const updateIdentifiers = (nextIdentifiers: Identifier[]) => {
    setValue('identifiers', nextIdentifiers, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const editSingleFieldSchema = (
    selectedFieldSchemaPath: string,
    {
      fieldSchema,
      identifierConfig,
    }: { fieldSchema: FieldSchemaEditorSupportedTypes; identifierConfig?: Identifier },
  ): { nextFieldSchemaPath: string } => {
    const { newSchemas, newPath } = replaceNestedField(
      activeFieldSchemas,
      selectedFieldSchemaPath,
      fieldSchema,
    );
    const { message, validForDraft } = validateSingleFieldSchema(fieldSchema, newSchemas);

    if (message === '') {
      clearErrors(`fieldSchemaMap.${selectedFieldSchemaPath}`);
    } else if (validForDraft) {
      // If user is editing label, the key updates between edits. We clear the error for the previous key and set the error on the new key
      // e.g. while user is typing label "Fraud rating" and field schema is invalid, when the user types 'u', we clear the error for key fra and set
      // the error for key frau
      clearErrors(`fieldSchemaMap.${selectedFieldSchemaPath}`);
      setError(`fieldSchemaMap.${newPath}`, { type: 'custom', message });
    } else {
      // if next field schema is invalid for draft, we cannot save it to form values so we save the error message to the
      // last known version of this field schema
      setError(`fieldSchemaMap.${selectedFieldSchemaPath}`, { type: 'custom', message });
    }

    if (validForDraft) {
      const nextIdentifiers =
        activeIdentifiers?.filter(
          (identifier) => identifier.key !== selectedFieldSchema(selectedFieldSchemaPath)?.key,
        ) || [];
      if (identifierConfig) {
        nextIdentifiers.push(identifierConfig);
      }

      setValue('fieldSchemaMap', fieldSchemasToFormValues(newSchemas), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('identifiers', nextIdentifiers, { shouldDirty: true });
      return { nextFieldSchemaPath: newPath };
    } else {
      return { nextFieldSchemaPath: selectedFieldSchemaPath };
    }
  };

  const resetSingleFieldSchema = () => {
    clearErrors();
    reset();
  };

  const submitSingleFieldSchema = (
    selectedFieldSchemaPath: string,
    {
      fieldSchema,
      identifierConfig,
    }: { fieldSchema: FieldSchemaEditorSupportedTypes; identifierConfig?: Identifier },
  ) => {
    const { newSchemas } = replaceNestedField(
      activeFieldSchemas,
      selectedFieldSchemaPath,
      fieldSchema,
    );
    const { message } = validateSingleFieldSchema(fieldSchema, newSchemas);

    if (message === '') {
      clearErrors();
    }

    const nextIdentifiers =
      activeIdentifiers?.filter(
        (identifier) => identifier.key !== selectedFieldSchema(selectedFieldSchemaPath)?.key,
      ) || [];
    if (identifierConfig) {
      nextIdentifiers.push(identifierConfig);
    }

    reset(
      {
        fieldSchemaMap: fieldSchemasToFormValues(newSchemas),
        identifiers: nextIdentifiers,
      },
      { keepIsValid: true },
    );

    if (message !== '') {
      setError(`fieldSchemaMap.${selectedFieldSchemaPath}`, { type: 'custom', message });
    }
  };

  const resetFormValues = useCallback((values: FieldSchemaEditorFormValues) => {
    reset({
      fieldSchemaMap: values.fieldSchemaMap,
      identifiers: values?.identifiers || [],
    });
  }, []);

  const getErrorMessage = (selectedFieldSchemaPath: string): string | undefined => {
    return get(formState.errors, `fieldSchemaMap.${selectedFieldSchemaPath}`)?.message;
  };

  return {
    activeFieldSchemaMap,
    activeFieldSchemas,
    activeIdentifiers,
    formFieldKeys,
    isFieldSchemasDirty,
    isEditingField: formState.isDirty,
    erroredKeys,
    deleteDraftField,
    validateAllFieldSchemas,
    updateFieldSchemas,
    addNewFieldSchema,
    submitSingleFieldSchema,
    editSingleFieldSchema,
    resetSingleFieldSchema,
    resetFormValues,
    updateIdentifiers,
    getErrorMessage,
    formState,
    setValue,
    ...formProps,
  };
}

export { useFieldSchemaEditor };
